import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { observer } from "mobx-react-lite";
import { useStableCallback } from "@/hooks/useStableCallback";
import { ArrowDown, ArrowUp, X } from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useIssuesStore } from "@/stores/pm/hooks/usePMStore";
import type { PMIssue } from "@/domains/pm/types";
import {
  groupAndSortIssues,
  type IssueGroupBy,
  type IssueSort,
  type IssueSortField,
} from "@/domains/pm/utils/groupAndSort";
import { useStatusCatalogue } from "@/domains/pm/statuses/statusCatalogueContext";
import {
  isOpenStatus,
  statusLabelMap,
  visibleStatusKeys,
} from "@/domains/pm/statuses/statusCatalogue";
import type { ViewerDocumentData } from "@/domains/documents/hooks/useDocumentViewer";
import { isIssueOverdue } from "@/domains/pm/utils/dueDate";
import { IssueGroup } from "./IssueGroup";
import { DraftIssueRow } from "./IssueRow";
import { useDraftIssueView, type DraftIssueViewProps } from "./issueDraftView";
import { cn } from "@/lib/utils";
import { applyPMRangeSelect } from "@/domains/pm/utils/rangeSelect";
import { useIssueLoadNotice } from "./useIssueLoadNotice";

interface IssuesListProps extends DraftIssueViewProps {
  buildingShortCode?: string;
  onSelectIssue: (issue: PMIssue) => void;
  selectedIssueId?: number;
  /** My Issues' Overdue tab — shows only open issues past their due date.
   *  Deliberately kept as a CLIENT-side re-filter on top of the server's
   *  `dueBefore`: isIssueOverdue works in local time while `dueBefore` is a
   *  date-string compare, and the two disagree either side of midnight.
   *  The Issues page applies overdue server-side and leaves this unset. */
  overdueOnly?: boolean;
  groupBy: IssueGroupBy;
  /** The project in scope, or null for "All projects" — decides which custom
   *  statuses group and how a status key resolves. */
  buildingProjectId?: number | null;
  sort: IssueSort;
  onSortChange: (field: IssueSortField) => void;
  openViewer: (doc: ViewerDocumentData) => Promise<void>;
  /** Ids currently checked for bulk actions (WHA-3334). */
  selectedIssueIds: ReadonlySet<number>;
  /** WHA-3456: the page's selection setter — lets the list apply both
   *  single-toggle and shift-click range selections. */
  setSelectedIssueIds: Dispatch<SetStateAction<Set<number>>>;
  /** WHA-3452: how many issues the filter control currently hides. */
  hiddenCount?: number;
  /** WHA-3452: resets the filter control (project + search untouched). */
  onClearFilters?: () => void;
}

interface Column {
  key: string;
  label: string;
  width: string;
  sortField?: IssueSortField;
}

export const IssuesList = observer(function IssuesList({
  buildingShortCode,
  onSelectIssue,
  selectedIssueId,
  overdueOnly,
  groupBy,
  buildingProjectId,
  sort,
  onSortChange,
  openViewer,
  selectedIssueIds,
  setSelectedIssueIds,
  hiddenCount,
  onClearFilters,
  ...draftProps
}: IssuesListProps) {
  const { t, language } = useLanguage();
  // New issues still being written. Pinned above the groups rather than filed
  // into one: under any grouping but the default the draft would land
  // somewhere arbitrary, and a collapsed group would hide it. Kept out of
  // `groups` entirely, so it never joins the shift-click range either.
  const drafts = useDraftIssueView(draftProps);
  const selectDraft = useStableCallback(drafts.select);
  const { catalogue: statusCatalogue } = useStatusCatalogue();
  const issuesStore = useIssuesStore();
  // WHA-3456: anchor row for shift-click range selection — the last
  // checkbox toggled without Shift.
  const [anchorId, setAnchorId] = useState<number | null>(null);
  // WHA-3452: split the "{count} issues hidden by filters" template so the
  // count can be emphasized.
  const [hiddenPre, hiddenPost] = t.pm.issues.filterBy.hiddenByFilters.split("{count}");
  // "Still loading" / "stopped at the ceiling" — see useIssueLoadNotice for
  // why an incomplete list must never look like a finished one.
  const loadNotice = useIssueLoadNotice();

  const visibleIssues = useMemo(
    () =>
      overdueOnly
        ? issuesStore.issues.filter((issue) =>
            isIssueOverdue(issue, (status) =>
              isOpenStatus(statusCatalogue, issue.building_project_id, status)
            )
          )
        : issuesStore.issues.slice(),
    [issuesStore.issues, overdueOnly, statusCatalogue]
  );

  const groups = useMemo(
    () =>
      groupAndSortIssues({
        issues: visibleIssues,
        // Pass the unfiltered list so orphan subissues can resolve the
        // parent's issue_number + title for the chip (WHA-3331).
        allIssues: issuesStore.issues.slice(),
        groupBy,
        sort,
        dateLabels: {
          today: t.pm.issues.dateBuckets.today,
          yesterday: t.pm.issues.dateBuckets.yesterday,
          thisWeek: t.pm.issues.dateBuckets.thisWeek,
          thisMonth: t.pm.issues.dateBuckets.thisMonth,
          older: t.pm.issues.dateBuckets.older,
          noDate: t.pm.issues.dateBuckets.noDate,
          overdue: t.pm.issues.dateBuckets.overdue,
          later: t.pm.issues.dateBuckets.later,
        },
        // From the catalogue, so a project's own statuses group and label
        // themselves. Every status any visible issue actually holds is added,
        // in case its row was deleted or belongs to another project.
        statusLabels: statusLabelMap(
          statusCatalogue,
          buildingProjectId,
          visibleIssues.map((i) => i.status)
        ),
        statusOrder: visibleStatusKeys(statusCatalogue, buildingProjectId),
        noDisciplineLabel: t.pm.issues.noDiscipline,
        noAssigneeLabel: t.pm.issues.noAssignee,
        language,
      }),
    [
      visibleIssues,
      issuesStore.issues,
      groupBy,
      sort,
      t,
      language,
      statusCatalogue,
      buildingProjectId,
    ]
  );

  // Looks up a loaded issue by id and forwards it to onSelectIssue —
  // wires the orphan-parent chip on a subissue row (WHA-3331).
  const handleJumpToIssue = useStableCallback((id: number) => {
    const target = issuesStore.issues.find((i) => i.id === id);
    if (target) onSelectIssue(target);
  });
  // Rows are memoised, so what they are handed must keep its identity: a fresh
  // handler on every render re-rendered every row on every click.
  const selectIssue = useStableCallback(onSelectIssue);
  const openRowViewer = useStableCallback(openViewer);

  // WHA-3456: flat, display-ordered list of issue ids across every group —
  // the index space a shift-click range is computed over.
  const orderedIds = useMemo(
    () => groups.flatMap((g) => g.items.map((item) => item.issue.id)),
    [groups]
  );

  // WHA-3456: checkbox toggle. A plain click toggles one issue and moves the
  // range anchor; Shift+click selects every issue between the anchor and the
  // clicked row (inclusive). Selecting accidental text during a shift-click
  // is cleared so the range gesture stays clean — a DOM effect, which is why
  // it lives here and not in the pure helper.
  const handleMultiSelect = useStableCallback((issueId: number, shiftKey: boolean) => {
    if (shiftKey && anchorId != null) window.getSelection?.()?.removeAllRanges();
    const next = applyPMRangeSelect({
      selected: selectedIssueIds,
      orderedIds,
      clickedId: issueId,
      anchorId,
      shiftKey,
    });
    setSelectedIssueIds(next.selected);
    setAnchorId(next.anchorId);
  });

  // Columns are defined here (not per-group) so every row across every
  // group participates in the same column sizing — fixes WHA-3315.
  const columns: Column[] = [
    { key: "checkbox", label: "", width: "w-8" },
    {
      key: "name",
      label: t.pm.issues.detail.issue,
      width: "min-w-[220px]",
      sortField: "issue_number",
    },
    { key: "status", label: t.pm.issues.detail.status, width: "", sortField: "status" },
    {
      key: "disciplines",
      label: t.pm.issues.detail.discipline,
      width: "",
      sortField: "discipline",
    },
    {
      key: "assignees",
      label: t.pm.issues.detail.assignees,
      width: "",
      sortField: "assignee",
    },
    {
      key: "dueDate",
      label: t.pm.issues.detail.dueDate,
      width: "",
      sortField: "due_date",
    },
    { key: "comments", label: t.pm.issues.detail.comments, width: "text-center" },
    { key: "document", label: t.pm.issues.detail.linkedDocuments, width: "max-w-[160px]" },
  ];

  return (
    <div className="px-7 pb-10">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-border/60">
              {columns.map((col) => {
                const sortField = col.sortField;
                const isSortable = !!sortField;
                const isActive = isSortable && sort.field === sortField;
                return (
                  <th
                    key={col.key}
                    className={cn(
                      "px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground",
                      col.width,
                      isSortable && "cursor-pointer select-none hover:text-foreground"
                    )}
                    onClick={sortField ? () => onSortChange(sortField) : undefined}
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      {isActive &&
                        (sort.direction === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        ))}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          {drafts.drafts.length > 0 && (
            <tbody data-draft-issues>
              {drafts.drafts.map((draft) => (
                <DraftIssueRow
                  key={draft.id}
                  issue={draft}
                  isActive={drafts.isActive(draft.id)}
                  isSaving={drafts.isSaving(draft.id)}
                  onSelect={selectDraft}
                />
              ))}
            </tbody>
          )}
          {groups.map((g) => (
            <IssueGroup
              key={g.key}
              label={g.label}
              count={g.items.length}
              items={g.items}
              defaultOpen={!g.defaultCollapsed}
              selectedIssueId={selectedIssueId}
              onSelectIssue={selectIssue}
              onJumpToIssue={handleJumpToIssue}
              buildingShortCode={buildingShortCode}
              colSpan={columns.length}
              openViewer={openRowViewer}
              selectedIssueIds={selectedIssueIds}
              onMultiSelectToggle={handleMultiSelect}
            />
          ))}
          {loadNotice && (
            <tbody>
              <tr className="border-t border-border/60">
                <td
                  colSpan={columns.length}
                  className="px-3 py-2.5 text-xs text-muted-foreground"
                  aria-live="polite"
                >
                  {loadNotice}
                </td>
              </tr>
            </tbody>
          )}
          {/* WHA-3452: footer — how many issues the filters hide + a reset. */}
          {hiddenCount != null && hiddenCount > 0 && (
            <tbody>
              <tr className="border-t border-border/60">
                <td colSpan={columns.length} className="px-3 py-2.5">
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span>
                      {hiddenPre}
                      <span className="font-medium text-foreground">{hiddenCount}</span>
                      {hiddenPost}
                    </span>
                    {onClearFilters && (
                      <button
                        type="button"
                        onClick={onClearFilters}
                        className="inline-flex items-center gap-1 font-medium text-muted-foreground hover:text-foreground"
                      >
                        {t.pm.issues.filterBy.clearFilters}
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>
    </div>
  );
});
